import * as React from "react"
import { Tabs as BaseTabs } from "@base-ui/react/tabs"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@lib/cn"

const TabsContext = React.createContext<{ variant: "line" | "pill" }>({ variant: "line" })

export interface RootProps
  extends React.ComponentPropsWithoutRef<typeof BaseTabs.Root> {
  variant?: "line" | "pill"
}

const Root = React.forwardRef<HTMLDivElement, RootProps>(
  ({ className, variant = "line", children, ...props }, ref) => (
    <TabsContext.Provider value={{ variant }}>
      <BaseTabs.Root ref={ref} className={cn("flex flex-col gap-3", className)} {...props}>
        {children}
      </BaseTabs.Root>
    </TabsContext.Provider>
  ),
)
Root.displayName = "Tabs.Root"

const List = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseTabs.List>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(TabsContext)
  return (
    <BaseTabs.List
      ref={ref}
      className={cn(
        "relative inline-flex items-center gap-1",
        variant === "line" && "border-b border-cloverhound-gray-250 dark:border-cloverhound-gray-800",
        variant === "pill" && "rounded-md bg-cloverhound-gray-100 p-1 dark:bg-cloverhound-gray-850",
        className,
      )}
      {...props}
    />
  )
})
List.displayName = "Tabs.List"

const tabVariants = cva(
  [
    "relative inline-flex items-center gap-1.5 px-3 text-small font-medium outline-none select-none",
    "transition-colors duration-quick ease-out-quad motion-reduce:transition-none",
    "focus-visible:ring-2 focus-visible:ring-cloverhound-indigo-500 focus-visible:ring-offset-2",
    "focus-visible:ring-offset-cloverhound-gray-50 dark:focus-visible:ring-offset-cloverhound-gray-950",
    "disabled:opacity-50 disabled:pointer-events-none",
  ].join(" "),
  {
    variants: {
      variant: {
        line: [
          "h-9 text-cloverhound-gray-700 dark:text-cloverhound-gray-400",
          "hover:text-cloverhound-gray-900 dark:hover:text-cloverhound-gray-50",
          "data-[active]:text-cloverhound-gray-900 dark:data-[active]:text-cloverhound-gray-50",
          "after:absolute after:-bottom-px after:left-0 after:right-0 after:h-0.5 after:bg-transparent",
          "data-[active]:after:bg-cloverhound-indigo-500",
        ].join(" "),
        pill: [
          "h-7 rounded-sm text-cloverhound-gray-700 dark:text-cloverhound-gray-400",
          "hover:text-cloverhound-gray-900 dark:hover:text-cloverhound-gray-50",
          "data-[active]:bg-white data-[active]:text-cloverhound-gray-900 data-[active]:shadow-xs",
          "dark:data-[active]:bg-cloverhound-gray-900 dark:data-[active]:text-cloverhound-gray-50",
        ].join(" "),
      },
    },
    defaultVariants: { variant: "line" },
  },
)

const Tab = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof BaseTabs.Tab> & VariantProps<typeof tabVariants>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(TabsContext)
  return <BaseTabs.Tab ref={ref} className={cn(tabVariants({ variant }), className)} {...props} />
})
Tab.displayName = "Tabs.Tab"

const Panel = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseTabs.Panel>
>(({ className, ...props }, ref) => (
  <BaseTabs.Panel
    ref={ref}
    className={cn(
      "outline-none focus-visible:ring-2 focus-visible:ring-cloverhound-indigo-500 rounded-md",
      className,
    )}
    {...props}
  />
))
Panel.displayName = "Tabs.Panel"

export const Tabs = { Root, List, Tab, Panel }
